import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { stockAPI } from "../services/api";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { useI18n } from "../i18n";
import { useMembership } from "../context/MembershipContext";
import QuickStockDialog from "../components/QuickStockDialog";
import TransferDialog from "../components/TransferDialog";
import EmptyState from "../components/EmptyState";

/**
 * Where the shelf — and the warehouse, if the shop has one — stand against
 * their alert levels, and what to buy or move.
 *
 * Two views of the same list: every product with its levels (which the owner
 * can set per product, or leave to follow the shop-wide one), and the shopping
 * list — what to bring from the supplier, and, with a warehouse, what to move
 * from it to the shelf. The register sells only the shelf, so a product that is
 * sold out in the shop but held in the warehouse needs moving, not buying.
 */
const STATUS_STYLE = {
  out: "bg-red-100 text-red-700",
  low: "bg-amber-100 text-amber-800",
  ok: "bg-green-100 text-green-700",
};

const statusOf = (row) => (row.is_out ? "out" : row.is_low || row.warehouse_low ? "low" : "ok");

const nameOf = (row) =>
  row.group_name && row.variant_label ? `${row.group_name} · ${row.variant_label}` : row.name;

/** An alert level typed in place: saved when the field is left, empty follows the default. */
const LevelInput = ({ row, field, placeholder, label, disabled, onChange }) => {
  const { t } = useI18n();
  const current = row[field] === null || row[field] === undefined ? "" : String(row[field]);
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setValue(current);
  }, [current]);

  const commit = async () => {
    const next = value.trim();
    if (next === current) return;

    setSaving(true);
    try {
      await onChange(row, { [field]: next === "" ? null : next });
    } finally {
      setSaving(false);
    }
  };

  return (
    <input
      type="number"
      min="0"
      step="1"
      inputMode="numeric"
      dir="ltr"
      value={value}
      disabled={disabled || saving}
      title={t("stock.alertHint")}
      aria-label={`${label} — ${row.name}`}
      placeholder={placeholder}
      onChange={(e) => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className="w-20 px-2 py-1 border border-gray-300 rounded text-end disabled:bg-gray-100 disabled:text-gray-400"
    />
  );
};

const Stock = () => {
  const { t } = useI18n();
  const { can, shop, isOwner } = useMembership();
  const [searchParams] = useSearchParams();
  // A reminder opens the shopping list directly
  const [tab, setTab] = useState(searchParams.get("tab") === "list" ? "list" : "all");
  const [data, setData] = useState({ shop_wide_threshold: 10, warehouse_enabled: false, products: [] });
  const [list, setList] = useState({ products: [], refill: [] });
  const [filters, setFilters] = useState({ q: "", category: "", low: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [stockProduct, setStockProduct] = useState(null);
  const [transfer, setTransfer] = useState(null);
  // Counting the warehouse: what the owner typed per product, until saved
  const [counts, setCounts] = useState(null);
  // Shown for selecting by hand when the browser refuses to copy
  const [manualCopy, setManualCopy] = useState("");

  const warehouse = Boolean(shop?.warehouse_enabled ?? data.warehouse_enabled);
  const canEdit = can("products.edit");
  const canMove = can("products.stock");

  const load = useCallback(async () => {
    try {
      const [overview, shopping] = await Promise.all([
        stockAPI.overview({ q: filters.q || undefined, category: filters.category || undefined }),
        stockAPI.shoppingList(),
      ]);
      setData(overview.data);
      setList(shopping.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("stock.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.category]);

  useEffect(() => {
    const timer = setTimeout(load, filters.q ? 250 : 0);
    return () => clearTimeout(timer);
  }, [load, filters.q]);

  const saveAlert = async (row, change) => {
    try {
      const { data: saved } = await stockAPI.setAlert(row.id, change);
      setError("");
      await load();
      return saved;
    } catch (err) {
      setError(err.response?.data?.message || t("stock.saveFailed"));
      await load();
      return undefined;
    }
  };

  const visible = useMemo(
    () => (filters.low ? data.products.filter((p) => p.is_low || p.warehouse_low) : data.products),
    [data.products, filters.low],
  );

  const counting = counts !== null;

  // When a count is lower than what was recorded, the owner says what happened
  // to the difference: it went back to the shop (a move), or it is gone (a loss
  // or a miscount, which lowers the total instead of inflating the shelf)
  const [askDown, setAskDown] = useState(0);

  const saveCounts = async (downAs) => {
    const changes = data.products
      .filter((row) => counts[row.id] !== undefined && counts[row.id] !== "" && /^\d+$/.test(counts[row.id]))
      .map((row) => ({ row, diff: Number(counts[row.id]) - row.warehouse_quantity }))
      .filter(({ diff }) => diff !== 0);

    const ups = changes.filter(({ diff }) => diff > 0);
    const downs = changes.filter(({ diff }) => diff < 0);

    if (changes.length === 0) {
      setCounts(null);
      return;
    }

    if (downs.length > 0 && typeof downAs !== "string") {
      setAskDown(downs.length);
      return;
    }

    const moves = [
      ...ups.map(({ row, diff }) => ({ product_id: row.id, to: "warehouse", quantity: diff })),
      ...(downAs === "shop"
        ? downs.map(({ row, diff }) => ({ product_id: row.id, to: "shop", quantity: -diff }))
        : []),
    ];

    try {
      if (moves.length > 0) await stockAPI.transfer(moves);

      if (downAs === "lost") {
        for (const { row, diff } of downs) {
          await stockAPI.adjustWarehouse(row.id, diff, t("stock.count.lostNote"));
        }
      }

      setNotice(t("stock.count.saved", { count: changes.length }));
      setCounts(null);
      setAskDown(0);
      setError("");
      await load();
    } catch (err) {
      setAskDown(0);
      setError(err.response?.data?.message || err.response?.data?.error || t("stock.count.failed"));
    }
  };

  const moveAllToShop = async () => {
    try {
      await stockAPI.transfer(
        list.refill.map((row) => ({ product_id: row.id, to: "shop", quantity: row.suggested_move })),
      );
      setNotice(t("stock.list.moved", { count: list.refill.length }));
      setError("");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.error || t("stock.transfer.failed"));
    }
  };

  const copyList = async () => {
    const lines = [
      t("stock.list.header"),
      ...list.products.map(
        (row) =>
          `• ${t("stock.list.line", { name: nameOf(row), count: row.suggested, stock: row.total_quantity })}`,
      ),
    ];

    if (warehouse && list.refill.length > 0) {
      lines.push("", t("stock.list.moveHeader"));
      list.refill.forEach((row) => lines.push(`• ${nameOf(row)} — ${row.suggested_move}`));
    }

    const text = lines.join("\n");

    try {
      await navigator.clipboard.writeText(text);
      setManualCopy("");
      setNotice(t("stock.list.copied"));
    } catch {
      setManualCopy(text);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  const toBuy = list.products;
  const toMove = list.refill;
  const attention = toBuy.length + toMove.length;

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 print:shadow-none">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">{t("stock.title")}</h1>
        <p className="text-gray-600 mt-1 max-w-2xl">
          {t(warehouse ? "stock.subtitleWarehouse" : "stock.subtitle")}
        </p>

        <div className="mt-4 flex gap-2 print:hidden" role="tablist">
          {["all", "list"].map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={`px-4 py-2 rounded-lg text-sm font-semibold ${
                tab === key ? "bg-primary-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {t(`stock.tabs.${key}`)}
              {key === "list" && attention > 0 && (
                <span className="ms-2 px-1.5 py-0.5 rounded-full bg-white/80 text-primary-700 text-xs">
                  {attention}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div role="alert" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          {notice}
        </div>
      )}

      {tab === "all" ? (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <input
              type="text"
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              placeholder={t("stock.search")}
              className="flex-1 min-w-48 px-4 py-2 border border-gray-300 rounded-lg"
            />
            <select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value })}
              className="px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="">{t("stock.allCategories")}</option>
              {PRODUCT_CATEGORIES.map((category) => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={filters.low}
                onChange={(e) => setFilters({ ...filters, low: e.target.checked })}
                className="h-4 w-4"
              />
              {t("stock.onlyLow")}
            </label>
            {warehouse && canMove && (
              <button
                type="button"
                onClick={() => setCounts(counting ? null : {})}
                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium"
              >
                {counting ? t("common.cancel") : t("stock.count.start")}
              </button>
            )}
          </div>

          {counting && (
            <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 text-sm text-gray-700 flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-2xl">{t("stock.count.hint")}</p>
              <button
                type="button"
                onClick={() => saveCounts()}
                className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
              >
                {t("stock.count.save")}
              </button>
            </div>
          )}

          {counting && askDown > 0 && (
            <div role="alertdialog" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-gray-800 space-y-3">
              <p className="font-medium">{t("stock.count.downAsk", { count: askDown })}</p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => saveCounts("shop")}
                  className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
                >
                  {t("stock.count.downShop")}
                </button>
                <button
                  type="button"
                  onClick={() => saveCounts("lost")}
                  className="px-4 py-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 font-semibold"
                >
                  {t("stock.count.downLost")}
                </button>
                <button type="button" onClick={() => setAskDown(0)} className="px-3 py-2 text-gray-600 hover:underline">
                  {t("common.cancel")}
                </button>
              </div>
            </div>
          )}

          <p className="text-xs text-gray-500">
            {t("stock.shopWide", { count: data.shop_wide_threshold })}{" "}
            {can("settings.view") && (
              <Link to="/admin/settings" className="text-primary-600 hover:underline">
                {t("stock.changeShopWide")}
              </Link>
            )}
          </p>

          {visible.length === 0 ? (
            <EmptyState screen="stock" message={t("stock.none")} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 border-b border-gray-200">
                    <th className="py-2 pe-3 text-start">{t("stock.columns.product")}</th>
                    <th className="py-2 pe-3 text-end">{t("stock.columns.inShop")}</th>
                    {warehouse && (
                      <>
                        <th className="py-2 pe-3 text-end">{t("stock.columns.inWarehouse")}</th>
                        <th className="py-2 pe-3 text-end">{t("stock.columns.total")}</th>
                      </>
                    )}
                    <th className="py-2 pe-3 text-start">{t("stock.columns.alertAt")}</th>
                    {warehouse && <th className="py-2 pe-3 text-start">{t("stock.columns.warehouseAlertAt")}</th>}
                    <th className="py-2 pe-3 text-start">{t("stock.columns.status")}</th>
                    <th className="py-2" />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((row) => (
                    <tr key={row.id} className="border-b border-gray-50">
                      <td className="py-2 pe-3">
                        <p className="font-medium text-gray-900">{nameOf(row)}</p>
                        {row.brand && <p className="text-xs text-gray-500">{row.brand}</p>}
                      </td>
                      <td className="py-2 pe-3 text-end font-semibold tabular-nums">{row.stock_quantity}</td>
                      {warehouse && (
                        <>
                          <td className="py-2 pe-3 text-end tabular-nums">
                            {counting ? (
                              <input
                                type="number"
                                min="0"
                                step="1"
                                inputMode="numeric"
                                dir="ltr"
                                aria-label={`${t("stock.columns.inWarehouse")} — ${row.name}`}
                                placeholder={String(row.warehouse_quantity)}
                                value={counts[row.id] ?? ""}
                                onChange={(e) => setCounts({ ...counts, [row.id]: e.target.value })}
                                className="w-20 px-2 py-1 border border-primary-300 rounded text-end"
                              />
                            ) : (
                              row.warehouse_quantity
                            )}
                          </td>
                          <td className="py-2 pe-3 text-end font-semibold tabular-nums">{row.total_quantity}</td>
                        </>
                      )}
                      <td className="py-2 pe-3">
                        <div className="flex items-center gap-2">
                          <LevelInput
                            row={row}
                            field="min_stock"
                            label={t("stock.columns.alertAt")}
                            placeholder={t("stock.alertPlaceholder", { count: data.shop_wide_threshold })}
                            disabled={!canEdit || row.low_stock_muted}
                            onChange={saveAlert}
                          />
                          {canEdit && (
                            <button
                              type="button"
                              title={row.low_stock_muted ? t("stock.unmute") : t("stock.mute")}
                              aria-label={`${row.low_stock_muted ? t("stock.unmute") : t("stock.mute")} — ${row.name}`}
                              aria-pressed={row.low_stock_muted}
                              onClick={() => saveAlert(row, { low_stock_muted: !row.low_stock_muted })}
                              className={`text-lg leading-none ${row.low_stock_muted ? "opacity-100" : "opacity-40 hover:opacity-100"}`}
                            >
                              🔕
                            </button>
                          )}
                        </div>
                      </td>
                      {warehouse && (
                        <td className="py-2 pe-3">
                          <LevelInput
                            row={row}
                            field="warehouse_min_stock"
                            label={t("stock.columns.warehouseAlertAt")}
                            placeholder={t("stock.noWarehouseAlert")}
                            disabled={!canEdit || row.low_stock_muted}
                            onChange={saveAlert}
                          />
                        </td>
                      )}
                      <td className="py-2 pe-3">
                        {row.low_stock_muted ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">
                            {t("stock.muted")}
                          </span>
                        ) : (
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLE[statusOf(row)]}`}>
                            {t(`stock.status.${statusOf(row)}`)}
                            {row.needs_refill && ` · ${t("stock.status.refill")}`}
                          </span>
                        )}
                      </td>
                      <td className="py-2 text-end whitespace-nowrap space-x-1 rtl:space-x-reverse">
                        {warehouse && canMove && (
                          <button
                            onClick={() => setTransfer({ row, name: nameOf(row) })}
                            className="px-3 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-700 font-medium"
                          >
                            {t("stock.move")}
                          </button>
                        )}
                        {canMove && (
                          <button
                            onClick={() =>
                              setStockProduct({ id: row.id, name: nameOf(row), stock_quantity: row.stock_quantity })
                            }
                            className="px-3 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 font-medium"
                          >
                            {t("stock.addStock")}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 space-y-6 print:shadow-none">
          <p className="text-sm text-gray-600 print:hidden">
            {t(warehouse ? "stock.list.introWarehouse" : "stock.list.intro")}
          </p>

          {attention === 0 ? (
            <p className="text-gray-500 py-8 text-center">{t("stock.list.empty")}</p>
          ) : (
            <>
              <div className="flex flex-wrap gap-2 print:hidden">
                <button
                  onClick={copyList}
                  className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
                >
                  {t("stock.list.copy")}
                </button>
                <button
                  onClick={() => window.print()}
                  className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                >
                  {t("stock.list.print")}
                </button>
                {isOwner && (
                  <Link
                    to="/admin/reminders?new=list"
                    className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-50 font-medium"
                  >
                    ⏰ {t("stock.list.remind")}
                  </Link>
                )}
              </div>

              {manualCopy && (
                <textarea
                  readOnly
                  rows={Math.min(manualCopy.split("\n").length + 1, 14)}
                  value={manualCopy}
                  onFocus={(e) => e.target.select()}
                  aria-label={t("stock.list.copy")}
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm font-mono print:hidden"
                />
              )}

              {warehouse && toMove.length > 0 && (
                <section>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                    <h2 className="text-lg font-semibold text-gray-800">{t("stock.list.moveTitle")}</h2>
                    {canMove && (
                      <button
                        onClick={moveAllToShop}
                        className="px-4 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold print:hidden"
                      >
                        {t("stock.list.moveAll")}
                      </button>
                    )}
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-200">
                        <th className="py-2 pe-3 text-start">{t("stock.list.product")}</th>
                        <th className="py-2 pe-3 text-end">{t("stock.columns.inShop")}</th>
                        <th className="py-2 pe-3 text-end">{t("stock.columns.inWarehouse")}</th>
                        <th className="py-2 text-end">{t("stock.list.toMove")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toMove.map((row) => (
                        <tr key={row.id} className="border-b border-gray-50">
                          <td className="py-2 pe-3 font-medium text-gray-900">{nameOf(row)}</td>
                          <td className="py-2 pe-3 text-end tabular-nums">{row.stock_quantity}</td>
                          <td className="py-2 pe-3 text-end tabular-nums">{row.warehouse_quantity}</td>
                          <td className="py-2 text-end font-bold tabular-nums">{row.suggested_move}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}

              {toBuy.length > 0 && (
                <section>
                  {warehouse && <h2 className="text-lg font-semibold text-gray-800 mb-2">{t("stock.list.buyTitle")}</h2>}
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-gray-500 border-b border-gray-200">
                        <th className="py-2 pe-3 text-start">{t("stock.list.product")}</th>
                        <th className="py-2 pe-3 text-end">
                          {t(warehouse ? "stock.columns.total" : "stock.list.inShop")}
                        </th>
                        <th className="py-2 text-end">{t("stock.list.suggested")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {toBuy.map((row) => (
                        <tr key={row.id} className="border-b border-gray-50">
                          <td className="py-2 pe-3 font-medium text-gray-900">{nameOf(row)}</td>
                          <td className="py-2 pe-3 text-end tabular-nums">{row.total_quantity}</td>
                          <td className="py-2 text-end font-bold tabular-nums">{row.suggested}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </section>
              )}
            </>
          )}
        </div>
      )}

      {stockProduct && (
        <QuickStockDialog
          product={stockProduct}
          onClose={() => setStockProduct(null)}
          onSaved={() => {
            setStockProduct(null);
            load();
          }}
        />
      )}

      {transfer && (
        <TransferDialog
          row={transfer.row}
          name={transfer.name}
          onClose={() => setTransfer(null)}
          onDone={({ name, to, quantity }) => {
            setTransfer(null);
            setNotice(t(`stock.transfer.done.${to}`, { name, count: quantity }));
            load();
          }}
        />
      )}
    </div>
  );
};

export default Stock;
